import React, {
  useRef,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Switch, Route, useLocation, Redirect } from "react-router-dom";
import Dashboard from "pages/dashboard/dashboard";
import DashboardRoot from "pages/dashboard/dashboard-root";
import QuickAccessPage from "pages/quick-access/quick-access";
import AccountsPage from "pages/accounts/accounts";
import PaymentsPage from "pages/payments/payments";
import RewardsPage from "pages/rewards/rewards";
import HelpPage from "pages/help/help";
import ProfilePage from "pages/profile/profile";
import PaymentMethodsPage from "pages/payment-methods/payment-methods";
import { MainStore } from "../../stores/Main";
import API from "../../utils/api";
import history from "../../utils/history";
import useModal from "../../hooks/use-modal";
import classNames from "classnames";
import DisclosurePage from "../../pages/disclosures/disclosures";
import Questionnaire from "../../pages/questionnaire/questionnaire";
import PaymentOptions from "../../pages/payment-options/payment-options";
import GeneralInfoPage from "../../pages/profile/general";
import LowerBillsPage from "../../pages/lowerbills/lowerbills";
import FinancingPage from "../../pages/financing/financing";
import WellnessCoachPage from "../../pages/wellnesscoach/wellness";
import JobSearchPage from "../../pages/jobsearch/job-search";
import ChatWidget, {
  ChatWidgetProvider,
  useChatWidget,
} from "../../blocks/chat-widget/chat-widget";
import SentimentQuestionnaire from "../../pages/sentiment-questionnaire/sentiment-questionnaire";
import { usePostHog } from "posthog-js/react";
// import SentimentQuestionnaire from "../../pages/sentiment-questionnaire/sentiment-questionnaire";

function useQuery(location) {
  return new URLSearchParams(location.search);
}

// Back-compat for the old chat page: /dashboard/chat now opens the persistent
// widget and lands on the dashboard. Only reachable post-disclosures
// (DashboardRoot redirects earlier states before children render).
function ChatOpenRedirect() {
  const { openChat } = useChatWidget();
  useEffect(() => {
    openChat();
    // openChat is idempotent, so StrictMode's dev double-invoke is harmless.
  }, [openChat]);
  return <Redirect to="/dashboard" />;
}

function App() {
  const { isOpen: isModalOpen } = useModal();
  const mainStoreContext = useContext(MainStore);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();
  const [learnMoreUrl, setLearnMoreUrl] = useState("");
  const query = useQuery(location);
  const initial = useRef(false);
  const posthog = usePostHog();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const getAppConfig = useCallback(async () => {
    try {
      const response = await API.get("/app_config");
      mainStoreContext.setAppConfig(response.data);
      if (!loaded) {
        setLoaded(true);
      }
    } catch (error) {}
  }, [loaded]);

  const fetchRefresh = useCallback(async () => {
    try {
      const response = await API.get("/refresh");
      if (response.data && response.data.url !== learnMoreUrl) {
        setLearnMoreUrl(response.data.url);
      }
      if (!mainStoreContext.isAuthenticated) {
        mainStoreContext.handleLoginSuccess({
          agency: response.data.agency,
          questionnaire: response.data.questionnaire,
          sentimentQuestionnaire: response.data.sentiment_questionnaire,
          phoneVerified: response.data.phone_verified,
          agencyLinked: response.data.agency_linked,
          userType: response.data.user_type,
        });
      } else {
        if (!mainStoreContext.phoneVerified && response.data.phone_verified) {
          mainStoreContext.handlePhoneVerified();
        }
        if (!mainStoreContext.questionnaire && response.data.questionnaire) {
          mainStoreContext.handleQuestionnaire();
        }
        if (
          !mainStoreContext.sentimentQuestionnaire &&
          response.data.sentiment_questionnaire
        ) {
          mainStoreContext.handleSentimentQuestionnaire();
        }
        if (!mainStoreContext.agencyLinked && response.data.agency_linked) {
          mainStoreContext.handleAgencyLinked();
        }
      }
      if (
        !mainStoreContext.acceptedDisclosures &&
        response.data.accepted_disc
      ) {
        console.log("accepted disc");
        mainStoreContext.handleAcceptDisc();
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.url !== learnMoreUrl
      ) {
        setLearnMoreUrl(error.response.data.url);
      }
      if (mainStoreContext.isAuthenticated) {
        mainStoreContext.handleLogout();
      }
      // Don't reset PostHog for unauthenticated users - we want to maintain their session
      if (
        !location.pathname.includes("/quick-access") &&
        location.pathname !== "/"
      ) {
        history.push("/");
      }
    }
    if (!initial.current) {
      // Fetch app details
      await getAppConfig();

      // Fetch agency details only on initial load
      try {
        const agencyResponse = await API.get("/agency_details");
        if (agencyResponse.data.name) {
          mainStoreContext.setAgencyDetails(agencyResponse.data);
          document.title = `${agencyResponse.data.name} - Powered by Freede`;
          initial.current = true;
        }
        posthog?.group("agency", agencyResponse.data._id || "freede", {
          name: agencyResponse.data.name || "Freede",
        });
      } catch (error) {}
    }
  }, [mainStoreContext, initial, loaded]);

  const refreshTimer = useRef();
  React.useEffect(() => {
    if (mainStoreContext.refCode === "") {
      const code = query.get("code");
      if (code !== "" && code !== undefined) {
        mainStoreContext.setRefCode(code);
      }
    }
    if (mainStoreContext.isAuthenticated) {
      fetchRefresh().then();
      clearInterval(refreshTimer.current);
      refreshTimer.current = setInterval(() => {
        fetchRefresh().then();
      }, 30000);
    } else {
      clearInterval(refreshTimer.current);
      fetchRefresh().then();
    }
    return () => clearInterval(refreshTimer.current);
  }, [mainStoreContext.isAuthenticated]);

  useEffect(() => {
    posthog?.capture("$pageview", { code: query.get("code") });
  }, [location]);

  if (loaded) {
    return (
      // ChatWidget sits OUTSIDE the Switch so it never unmounts on route
      // changes — its iframe holds the live chat websocket. (The is-blur
      // class is currently inert — no matching `.is-blur`-only rule — but
      // never add a real filter here: a filter on an ancestor becomes the
      // containing block for position:fixed and would break the widget.)
      <ChatWidgetProvider>
        <div className={classNames({ "is-blur": isModalOpen })}>
          <Switch>
          <Route
            exact
            path="/"
            render={() =>
              mainStoreContext.isAuthenticated ? (
                mainStoreContext.userType === "consumer-quick-access" &&
                !mainStoreContext.acceptedDisclosures ? (
                  // Only PRE-acceptance: once disclosures are accepted,
                  // landing on "/" (e.g. after a questionnaire submit)
                  // showed the disclosures page again.
                  <Redirect to="/dashboard/disclosures" />
                ) : (
                  <Redirect to="/dashboard" />
                )
              ) : (
                <QuickAccessPage />
              )
            }
          />
          <Route exact path="/quick-access" component={QuickAccessPage} />
          <Route exact path="/questionnaire" component={Questionnaire} />
          <Route
            exact
            path="/sentiment-questionnaire"
            component={SentimentQuestionnaire}
          />
          <Route exact path="/payment-options" component={PaymentOptions} />
          <Route path="/dashboard">
            <DashboardRoot isAuthenticated={mainStoreContext.isAuthenticated}>
              <Switch>
                <Route
                  exact
                  path="/dashboard/profile"
                  component={ProfilePage}
                />
                <Route
                  exact
                  path="/dashboard/profile/general"
                  component={GeneralInfoPage}
                />
                {/* The dashboard overview page is retired for agency users —
                    Accounts is the landing. Non-agency sessions keep the old
                    Dashboard (their accounts routes aren't registered). */}
                <Route
                  exact
                  path="/dashboard"
                  render={() =>
                    mainStoreContext.agency ? (
                      <Redirect to="/dashboard/accounts" />
                    ) : (
                      <Dashboard />
                    )
                  }
                />
                <Route
                  exact
                  path="/dashboard/disclosures"
                  component={DisclosurePage}
                />
                <Route
                  exact
                  path="/dashboard/payments/methods"
                  component={PaymentMethodsPage}
                />
                <Route
                  exact
                  path="/dashboard/lowerbills"
                  component={LowerBillsPage}
                />
                <Route
                  exact
                  path="/dashboard/financing"
                  component={FinancingPage}
                />
                <Route
                  exact
                  path="/dashboard/wellness-coach"
                  component={WellnessCoachPage}
                />
                <Route
                  exact
                  path="/dashboard/job-search"
                  component={JobSearchPage}
                />
                <Route
                  exact
                  path="/dashboard/chat"
                  component={ChatOpenRedirect}
                />
                {mainStoreContext.agency && (
                  <>
                    <Route
                      exact
                      path="/dashboard/accounts"
                      component={AccountsPage}
                    />
                    <Route
                      exact
                      path="/dashboard/accounts/dispute"
                      component={AccountsPage}
                    />
                    <Route
                      exact
                      path="/dashboard/accounts/attorney"
                      component={AccountsPage}
                    />
                    <Route
                      exact
                      path="/dashboard/accounts/bankruptcy"
                      component={AccountsPage}
                    />
                    <Route
                      exact
                      path="/dashboard/payments"
                      component={PaymentsPage}
                    />
                  </>
                )}
                <Redirect from="*" to="/dashboard" />
              </Switch>
            </DashboardRoot>
          </Route>
          </Switch>
          <ChatWidget />
        </div>
      </ChatWidgetProvider>
    );
  } else {
    return null;
  }
}

export default App;
